export class Color {
  private r = 0;
  private g = 0;
  private b = 0;
  public get red(): number {
    return Math.round(this.r * 255);
  }
  public get green(): number {
    return Math.round(this.g * 255);
  }
  public get blue(): number {
    return Math.round(this.b * 255);
  }

  private h = 0;
  private s = 0;
  private l = 0;
  public get hue(): number {
    return this.h;
  }
  public get saturation(): number {
    return this.s;
  }
  public get lightness(): number {
    return this.l;
  }

  private a = 1;
  public get alpha(): number {
    return this.a;
  }

  public get RGB(): number[] {
    return [this.red, this.green, this.blue];
  }
  public get RGBA(): number[] {
    return [...this.RGB, this.a];
  }

  public get HSL(): string[] {
    const hue = `${Math.round(360 * this.h)}`;
    const saturation = `${Math.round(100 * this.s)}%`;
    const lightness = `${Math.round(100 * this.l)}%`;
    return [hue, saturation, lightness];
  }
  public get HSLA(): string[] {
    return [...this.HSL, `${this.a}`];
  }

  private toHex(value: number): string {
    const prefix = 255 * value < 16 ? '0' : '';
    return `${prefix}${Math.round(255 * value).toString(16)}`;
  }

  public get HEX(): string[] {
    return [this.toHex(this.r), this.toHex(this.g), this.toHex(this.b)];
  }

  public get cssRGB(): string {
    return `rgb(${this.RGB.join(', ')})`;
  }
  public get cssRGBA(): string {
    return `rgba(${this.RGBA.join(', ')})`;
  }
  public get cssHSL(): string {
    return `hsl(${this.HSL.join(', ')})`;
  }
  public get cssHSLA(): string {
    return `hsl(${this.HSLA.join(', ')})`;
  }
  public get cssHEX(): string {
    return `#${this.HEX.join('')}`.toUpperCase();
  }
  public get cssHEXA(): string {
    return `#${this.HEX.join('')}${this.toHex(this.a)}`.toUpperCase();
  }

  public static makeRGB(
    red: number,
    green: number,
    blue: number,
    alpha?: number,
  ): Color {
    const color = new Color();
    color.r = this.sanitizeRGB(red);
    color.g = this.sanitizeRGB(green);
    color.b = this.sanitizeRGB(blue);
    if (alpha !== undefined && alpha !== null) {
      color.a = alpha;
    }
    this.rgbToHsl(color);
    return color;
  }

  public static makeHSL(
    hue: string | number,
    saturate: string | number,
    lightness: string | number,
    alpha?: number,
  ): Color {
    const color = new Color();
    color.h = this.sanitizeHue(hue);
    color.s = this.sanitizeSaturation(saturate);
    color.l = this.sanitizeLightness(lightness);
    if (alpha !== undefined && alpha !== null) {
      color.a = alpha;
    }

    this.hslToRgb(color);
    return color;
  }
  public static makeHEX(value: string): Color {
    const regex = new RegExp(
      /^#?([aA-fF0-9]{6}|[aA-fF0-9]{3}|[aA-fF0-9]{8}|[aA-fF0-9]{4})$/,
    );
    if (!value.match(regex)) {
      throw new Error(`Error: Invalid value "${value}" format for hex format.`);
    }
    if (value.startsWith('#')) {
      value = value.replace('#', '');
    }
    const color = new Color();
    switch (value.length) {
      case 3:
        color.r = this.sanitizeRGB(
          parseInt(value.substring(0, 1) + value.substring(0, 1), 16),
        );
        color.g = this.sanitizeRGB(
          parseInt(value.substring(1, 2) + value.substring(1, 2), 16),
        );
        color.b = this.sanitizeRGB(
          parseInt(value.substring(2, 3) + value.substring(2, 3), 16),
        );
        break;
      case 4:
        color.r = this.sanitizeRGB(
          parseInt(value.substring(0, 1) + value.substring(0, 1), 16),
        );
        color.g = this.sanitizeRGB(
          parseInt(value.substring(1, 2) + value.substring(1, 2), 16),
        );
        color.b = this.sanitizeRGB(
          parseInt(value.substring(2, 3) + value.substring(2, 3), 16),
        );
        color.a =
          parseInt(value.substring(3, 4) + value.substring(3, 4), 16) / 255;
        break;
      case 6:
        color.r = this.sanitizeRGB(parseInt(value.substring(0, 2), 16));
        color.g = this.sanitizeRGB(parseInt(value.substring(2, 4), 16));
        color.b = this.sanitizeRGB(parseInt(value.substring(4, 6), 16));
        break;
      case 8:
        color.r = this.sanitizeRGB(parseInt(value.substring(0, 2), 16));
        color.g = this.sanitizeRGB(parseInt(value.substring(2, 4), 16));
        color.b = this.sanitizeRGB(parseInt(value.substring(4, 6), 16));
        color.a = parseInt(value.substring(6, 8), 16) / 255;
        break;
    }
    this.rgbToHsl(color);
    return color;
  }

  public static parse(value: string): Color {
    if (typeof value === 'undefined') {
      return null;
    }
    if ((value = value.trim().toLowerCase()).length === 0) {
      return null;
    }
    if (value.startsWith('#')) {
      return Color.makeHEX(value);
    }

    if (value.startsWith('rgb')) {
      const parts = value.match(
        /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,?\s*(\d*(?:\.\d+\s*)?)\)$/,
      );
      if (!parts) {
        throw new Error(`Unrecognized color format (2): ${value}`);
      }
      if (!parts[4]) {
        return Color.makeRGB(
          parseInt(parts[1], 10),
          parseInt(parts[2], 10),
          parseInt(parts[3], 10),
        );
      }
      return Color.makeRGB(
        parseInt(parts[1], 10),
        parseInt(parts[2], 10),
        parseInt(parts[3], 10),
        parseFloat(parts[4]),
      );
    }
    return undefined;
  }

  private static sanitizeRGB(color: number | string): number {
    // If string and cannot be parsed to number => Error
    if (typeof color == 'string' && Number.isNaN(parseInt(color, 10))) {
      throw new Error('Bad format');
    }
    // Case String %
    if (typeof color === 'string' && color.includes('%')) {
      color = parseInt(color, 10);
      if (color < 0 || color > 100) {
        throw new Error('Bad format');
      }
      return color / 100;
    }
    if (typeof color === 'string') {
      color = parseInt(color, 10);
    }
    if (color < 0) {
      throw new Error('Bad format');
    }
    if (color >= 0 && color <= 1) {
      return color;
    }
    if (color >= 1 && color < 256) {
      return color / 255;
    }
    throw new Error(`Bad format ('${color}')`);
  }
  private static sanitizeHue(hue: number | string): number {
    if (typeof hue === 'string' && Number.isNaN((hue = parseFloat(hue)))) {
      throw new Error('Bad format');
    }
    if (hue < 0 || hue > 360) {
      throw new Error('Hue out of range (0..360)');
    }
    const indexOf = `${hue}`.indexOf('.');
    if ((indexOf > -1 && hue > 1) || indexOf === -1) {
      hue /= 360;
    }
    return hue;
  }
  private static sanitizeSaturation(saturation: number | string): number {
    if (typeof saturation === 'string' && saturation.includes('%')) {
      saturation = parseInt(saturation, 10);
      if (saturation < 0 || saturation > 100) {
        throw new Error('Bad format for saturation');
      }
      return saturation / 100;
    }
    if (typeof saturation === 'string') {
      saturation = parseInt(saturation, 10);
    }
    if (saturation < 0 || saturation > 1) {
      throw new Error('Bad format for saturation');
    }
    return saturation;
  }
  private static sanitizeLightness(lightness: number | string): number {
    if (typeof lightness === 'string' && lightness.includes('%')) {
      lightness = parseInt(lightness, 10);
      if (lightness < 0 || lightness > 100) {
        throw new Error('Bad format for lightness');
      }
      return lightness / 100;
    }
    if (typeof lightness === 'string') {
      lightness = parseInt(lightness, 10);
    }
    if (lightness < 0 || lightness > 1) {
      throw new Error('Bad format for lightness');
    }
    return lightness;
  }

  private static rgbToHsl(color: Color): void {
    const r: number = color.r;
    const g: number = color.g;
    const b: number = color.b;
    const max: number = Math.max(r, g, b);
    const min: number = Math.min(r, g, b);
    color.l = (max + min) / 2;
    if (max === min) {
      color.h = color.s = 0; // achromatic
    } else {
      const d = max - min;
      color.s = color.l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          color.h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          color.h = (b - r) / d + 2;
          break;
        case b:
          color.h = (r - g) / d + 4;
          break;
      }
      color.h /= 6;
    }
  }
  private static hslToRgb(color: Color): void {
    const h: number = color.h;
    const s: number = color.s;
    const l: number = color.l;
    if (s === 0) {
      color.r = color.g = color.b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      color.r = this.hueToRgb(p, q, h + 1 / 3);
      color.g = this.hueToRgb(p, q, h);
      color.b = this.hueToRgb(p, q, h - 1 / 3);
    }
  }
  private static hueToRgb(p: number, q: number, t: number): number {
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
      return q;
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
  }

  public saturate(value: string | number): void {
    if (
      typeof value === 'string' &&
      value.includes('%') &&
      !Number.isNaN((value = parseInt(value, 10)))
    ) {
      this.s += value / 100;
    } else if (typeof value === 'number') {
      this.s += value / 255;
    } else {
      throw new Error('error: bad modifier format (percent or number)');
    }
    if (this.s > 1) {
      this.s = 1;
    } else if (this.s < 0) {
      this.s = 0;
    }
    Color.hslToRgb(this);
  }
  public desaturate(value: string | number): void {
    this.saturate(`-${value}`);
  }
  public lighten(value: string | number): void {
    if (
      typeof value === 'string' &&
      value.includes('%') &&
      !Number.isNaN((value = parseInt(value, 10)))
    ) {
      this.l += value / 100;
    } else if (typeof value === 'number') {
      this.l += value / 255;
    } else {
      throw new Error('error: bad modifier format (percent or number)');
    }
    if (this.l > 1) {
      this.l = 1;
    } else if (this.l < 0) {
      this.l = 0;
    }
    Color.hslToRgb(this);
  }
  public darken(value: string | number): void {
    this.lighten(`-${value}`);
  }
  public fadein(value: string | number): void {
    if (
      typeof value === 'string' &&
      value.includes('%') &&
      !Number.isNaN((value = parseInt(value, 10)))
    ) {
      this.a += value / 100;
    } else if (typeof value === 'number') {
      this.a += value / 255;
    } else {
      throw new Error('error: bad modifier format (percent or number)');
    }
    if (this.a > 1) {
      this.a = 1;
    } else if (this.a < 0) {
      this.a = 0;
    }
    Color.hslToRgb(this);
  }
  public fadeOut(value: string | number): void {
    this.fadein(`-${value}`);
  }
  public clone(): Color {
    return Color.makeRGB(this.r, this.g, this.b, this.a);
  }
  public interpolate(c: Color, t: number): Color {
    this.r += (c.r - c.r) * t;
    this.g += (c.g - c.g) * t;
    this.b += (c.b - c.b) * t;
    this.a += (c.a - c.a) * t;
    return this;
  }
  public toSearchColor(): void {
    this.l = 0.5;
    this.s = 1;
  }

  public static primaryColor = {
    orangeInf: Color.makeHEX('#faaa0a'),
    blackInf: Color.makeHEX('#2f373a'),
    orangeErp: Color.makeHEX('#faaa0a'),
    orangeErpLightHover: Color.makeHEX('#ffce54'),
    orangeErpDarkHover: Color.makeHEX('#f0960a'),
    blueMobilesSolutions: Color.makeHEX('#009fff'),
    blueMobilesSolutionsLightHover: Color.makeHEX('#3fb9ff'),
    blueMobilesSolutionsDarkHover: Color.makeHEX('#007fff'),
    greenServiceIT: Color.makeHEX('#dedc00'),
    greenServiceITLightHover: Color.makeHEX('#e6e155'),
    greenServiceITDarkHover: Color.makeHEX('#bec300'),
  };
  public static secondaryColor = {
    black: Color.makeHEX('#4a555c'),
    black2: Color.makeHEX('#181c1e'),
    black3: Color.makeHEX('#090c0d'),
    grey: Color.makeHEX('#fafafa'),
    grey2: Color.makeHEX('#c8c8c8'),
    red: Color.makeHEX('#ed5565'),
    redHover: Color.makeHEX('#da4453'),
    orange: Color.makeHEX('#fc6e51'),
    orangeHover: Color.makeHEX('#e9573f'),
    yellow: Color.makeHEX('#ffce54'),
    yellowHover: Color.makeHEX('#f6bb42'),
    green: Color.makeHEX('#48cfad'),
    greenHover: Color.makeHEX('#37bc9b'),
    blue: Color.makeHEX('#4fc1e9'),
    blueHover: Color.makeHEX('#3bafda'),
    mauve: Color.makeHEX('#ac92ec'),
    mauveHover: Color.makeHEX('#967adc'),
    purple: Color.makeHEX('#ec87c0'),
    purpleHover: Color.makeHEX('#d770ad'),
    brown: Color.makeHEX('#a08053'),
    brownHover: Color.makeHEX('#806540'),
  };
}
